import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { transformRecordFromServer } from 'helpers/server-data-transformers';

export interface CurrentUserData {
  id: string | number;
  oktaUserId: string;
  authority?: string;
  client?: {
    id: string | number;
    consultants?: unknown[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface CurrentUserState {
  id: string | number | null;
  data: CurrentUserData | null;
  loading: boolean;
  error: unknown;
  filesCount: number;
  insightsCount: number;
  changingPassword?: boolean;
  passwordChangeError?: unknown;
}

const initialState: CurrentUserState = {
  id: null,
  data: null,
  loading: false,
  error: false,
  filesCount: 0,
  insightsCount: 0,
};

const currentUserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loadingCurrentUser: (state) => {
      state.loading = true;
      state.error = false;
    },
    loadingCurrentUserFailed: (state, { payload: error }: PayloadAction<unknown>) => {
      state.loading = false;
      state.error = error;
    },
    setCurrentUser: (state, { payload }: PayloadAction<CurrentUserData>) => {
      state.id = payload.id;
      // Store this here for ease of access, but make sure to update this
      // whenever the user record is updated (see extraReducers)
      state.data = transformRecordFromServer(
        'user',
        payload,
      ) as CurrentUserData;
      state.loading = false;
      state.error = false;
    },
    changingPassword: (state) => {
      state.changingPassword = true;
      state.passwordChangeError = false;
    },
    changingPasswordFailed: {
      reducer: (state, { payload }: PayloadAction<unknown>) => {
        state.changingPassword = false;
        state.passwordChangeError = payload;
      },
      prepare: (passwordChangeError?: unknown) => ({
        payload: passwordChangeError ?? false,
      }),
    },
    changingPasswordSucceeded: (state) => {
      state.changingPassword = false;
      state.passwordChangeError = false;
    },
    setNewFilesCountSucceeded: (state, { payload }: PayloadAction<{ count?: number }>) => {
      state.filesCount = payload?.count ?? 0;
    },
    setNewInsightsCountSucceeded: (
      state,
      { payload }: PayloadAction<{ count?: number }>,
    ) => {
      state.insightsCount = payload?.count ?? 0;
    },
    decrementInsightsCount: (state) => {
      if (state.insightsCount > 0) {
        state.insightsCount -= 1;
      }
    },
  },
});

export const { actions } = currentUserSlice;

export default currentUserSlice.reducer;
